import { components } from '@/generated/internal-api/openapi-types';
import { GroupApiId, StudyGroupV1 } from '@/model/TreatmentGroup.model';
import { AnimalApiId, AnimalDefinitionV1, AnimalV1 } from '@/model/Animal.model';
import { Nullable, TempApiID } from '@/model/Common.model';
import { AnimalDefinitionApiId } from '@/model/PlannedAnimal.model';
import { MetadataGlossaryApiId } from '@/model/Metadata.model';

export type SampleDefinitionDetailType = components['schemas']['SampleDefinitionDetailType.schema'];
export type SampleDefinitionApiId = components['schemas']['SampleDefinitionApiId.schema'];
export type SampleDefinitionDetailV1 = components['schemas']['SampleDefinitionDetailV1.schema'];
export type SampleDefinitionMetadataV1 = components['schemas']['SampleDefinitionMetadataV1.schema'];
export type SampleDefinitionDetailUnit = components['schemas']['SampleDefinitionDetailUnit.schema'];
export type SampleDefinitionV1 = components['schemas']['SampleDefinitionV1.schema'];
export type SampleDefinitionUpsertV1 = components['schemas']['SampleDefinitionUpsertV1.schema'];
export type SampleDefinitionCreateV1 = components['schemas']['SampleDefinitionCreateV1.schema'];
export type SamplePlanExportType = components['schemas']['SamplePlanExportType.schema'];
export type CollectionStatus = components['schemas']['SampleCollectionStatus.schema'];
export type PlannedSampleSheetCreateV1 = components['schemas']['PlannedSampleSheetCreateV1.schema'];
export type SamplingSheetBaseColumn = components['schemas']['SamplingSheetBaseColumn.schema'];
export type SamplingSheetColumnId = SamplingSheetBaseColumn | MetadataGlossaryApiId;

export const COLLECTION_STATUS_TOOLTIPS: Record<CollectionStatus, string | undefined> = {
  not_configured: 'Sample type group assignment not configured',
  unavailable: 'No registered animals available for sample collection',
  available: undefined,
};

export const plannedSampleExportType: Record<SamplePlanExportType, SamplePlanExportType> = {
  sample_plan: 'sample_plan',
} as const;

/**
 * Planned Sample Form Data
 */

export type TempSampleTypeId = TempApiID<'tgl'>;
export type TempSampleDefinitionApiId = TempApiID<'sdf'>;
export type TempGroupApiId = components['schemas']['TempGroupApiId.schema'];
export type TempAnimalApiId = components['schemas']['TempAnimalApiId.schema'];
export type TempAnimalDefinitionApiId = components['schemas']['TempAnimalDefinitionApiId.schema'];
export type GroupTarget = Pick<StudyGroupV1, 'color' | 'name' | 'max_subjects' | 'no'> & {
  api_id: GroupApiId | TempGroupApiId;
};
export type RegisteredAnimalTarget = Pick<AnimalV1, 'name'> & {
  api_id: AnimalApiId | TempAnimalApiId;
  study_group_api_id: Nullable<GroupTarget['api_id']>;
};
export type PlannedAnimalTarget = Pick<AnimalDefinitionV1, 'planned_id' | 'animal_api_id'> & {
  api_id: AnimalDefinitionApiId | TempAnimalDefinitionApiId;
  study_group_api_id: GroupTarget['api_id'];
};
export type TargetApiId = GroupTarget['api_id'] | RegisteredAnimalTarget['api_id'] | PlannedAnimalTarget['api_id'];
type TargetApiIdMap = {
  groups: GroupTarget['api_id'];
  registered_animals: RegisteredAnimalTarget['api_id'];
  planned_animals: PlannedAnimalTarget['api_id'];
  no_target: never;
};
export type SampleDefinitionTargetFormData =
  | {
      [K in keyof Omit<TargetApiIdMap, 'no_target'>]: {
        type: K;
        targets: Array<TargetApiIdMap[K]>;
      };
    }[keyof Omit<TargetApiIdMap, 'no_target'>]
  | { type: 'no_target' };
export type SampleDefinitionFormData = Omit<SampleDefinitionV1, 'api_id' | 'target' | 'created_at' | 'updated_at'> & {
  api_id: SampleDefinitionApiId | TempSampleDefinitionApiId;
  target: SampleDefinitionTargetFormData;
};

export const CollectionMethod = {
  quick_collect: 'quick_collect',
  sampling_sheet: 'sampling_sheet',
} as const;

export type CollectionMethodType = (typeof CollectionMethod)[keyof typeof CollectionMethod];
